import { Text } from 'react-native'
import React, { Suspense, useCallback, useRef } from 'react'
import { useIsFocused } from '@react-navigation/native'
import { useQuery } from '@tanstack/react-query'
import { ScreenLayout, SecureStore } from '@libs/utils'
import DesktopView from './DesktopView'
import MobileView from './MobileView'
import {
  generateOuthAccessToken,
  getOpportunitiesById,
  openUrl,
  updateObjects,
} from '../../api'
import {
  getBrandIframeConfig,
  usesCustomIframeWorkflow,
} from '../../utils/getOAPUrl'
import { generateSignedData } from '../../utils/generateSignedData'

const NewOapApplication = (props) => {
  const LayoutView = useCallback(
    ScreenLayout.withLayoutView(DesktopView, MobileView, MobileView),
    [],
  )

  const { isDashboard, id: opportunityId, mode } = props.route.params

  const isFocused = useIsFocused()
  const scrollViewRef = useRef(null)

  const fetchOpportunities = async () => {
    let userProfile = await SecureStore.getItemAsync('userProfile')
    userProfile = JSON.parse(userProfile)

    const data = await getOpportunitiesById({
      opportunityId,
      email: userProfile?.email,
    })
    const finalData = data?.response?.[0]

    return finalData
  }

  const { data: opportunities, isFetching: isOpportunitiesFetching } = useQuery(
    {
      queryKey: [`getOpportunities-details-${opportunityId}`],
      queryFn: fetchOpportunities,
      enabled: isFocused && !!opportunityId,
      initialData: [],
    },
  )

  const dateTime = new Date().toISOString().slice(0, 19).replace('T', ' ')

  const generatePortalUrl = () => {
    const createPayload = {
      dateTime,
      externalId: opportunities?.ApplicationFormId__c,
      clientId: 'apphero',
      sfProgramId: opportunities?.Programme__c,
      email: opportunities?.Account?.PersonEmail || opportunities?.PK,
      firstName: opportunities?.Account?.FirstName,
      lastName: opportunities?.Account?.LastName,
      phone: opportunities?.Account?.Phone,
      country:
        opportunities?.Account?.Country__c ||
        opportunities?.Account?.PersonMailingCountry,
    }

    const brandConfig = getBrandIframeConfig(opportunities)

    const signature = generateSignedData(
      createPayload,
      opportunities.BusinessUnitFilter__c,
    )

    const { baseUrl, createApiEndpoint: apiEndpoint } = brandConfig.config
    const createApplicationUrl = `${baseUrl}${apiEndpoint}?signed_data=${encodeURIComponent(
      signature,
    )}`

    openUrl({ url: createApplicationUrl })

    return createApplicationUrl
  }

  const { data: tokens, isFetching: isTokensFetching } = useQuery({
    queryKey: ['generateOuthAccessToken', opportunities?.Id],
    queryFn: () =>
      usesCustomIframeWorkflow(opportunities)
        ? () => {}
        : generateOuthAccessToken(opportunities?.BusinessUnitFilter__c),
    enabled:
      isFocused &&
      !!opportunities?.Id &&
      !!opportunities?.BusinessUnitFilter__c &&
      !opportunities?.ApplicationSubmitted__c,
  })

  const updateOpportunity = async () => {
    const payload = {
      Id: opportunities?.Id,
      isSubmittedFromAppHero: true,
      email: opportunities?.Account?.PersonEmail,
    }
    const response = await updateObjects(payload, 'opportunity')
    if (response?.ok) {
      return response
    }
  }

  const viewProps = {
    opportunities,
    isOpportunitiesFetching,
    scrollViewRef,
    tokens,
    updateOpportunity,
    isTokensFetching,
    generatePortalUrl,
    backText: isDashboard ? 'BACK TO DASHBOARD' : 'BACK TO APPLICATIONS',
    isDashboard,
    mode,
  }

  return (
    <Suspense fallback={<Text>Loading</Text>}>
      <LayoutView {...viewProps} />
    </Suspense>
  )
}

export default NewOapApplication
